'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useDebounce } from '@/hooks/useDebounce';
import { useClickOutside } from '@/hooks/useClickOutside';
import { EventService } from '@/services/events';
import type { Event } from '@/types/api';
import { EventFilters } from '@/types/filters';
import { config } from '@/config/config';
import { XIcon, SearchIcon } from 'lucide-react';
import useProvinces from '@/hooks/cachedCalls/use-states';
import { capitalizeString } from '@/utils/formating';
import { createSportSlug, createCategorySlug } from '@/utils/slugs';
import { getSportDefaultImage } from '@/utils/images';
import { hasAdvertisementConsent } from '@/utils/cookieConsent';
import { getFacebookBrowserIds } from '@/utils/facebook-capi';
// Alias: `eventService.trackSearch` (Facebook CAPI) ya ocupa ese nombre en este fichero.
import { trackSearch as trackGa4Search } from '@/utils/analytics';
import EventCard from './EventCard';
import { useEventsRegistrationAvailability } from '@/hooks/use-events-registration-availability';
import { useRouter } from 'next/navigation';
import { useCachedSports } from '@/hooks/useCachedSports';

// Importar las traducciones de deportes
import enTranslations from '@/locales/en/common.json';
import esTranslations from '@/locales/es/common.json';
import ptTranslations from '@/locales/pt/common.json';
import euTranslations from '@/locales/eu/common.json';
import caTranslations from '@/locales/ca/common.json';
import frTranslations from '@/locales/fr/common.json';
import itTranslations from '@/locales/it/common.json';

// Función para obtener las traducciones según el idioma
const getTranslations = (lang: string) => {
    const allTranslations = {
        en: enTranslations,
        es: esTranslations,
        pt: ptTranslations,
        eu: euTranslations,
        ca: caTranslations,
        fr: frTranslations,
        it: itTranslations
    };
    return allTranslations[lang as keyof typeof allTranslations] || allTranslations.en;
};

interface SearchBarProps {
    placeholder?: string;
    className?: string;
    lang: string;
}

// Interfaces para manejar deportes
interface SportResult {
    id: string;
    name: string;
    type: 'sport';
    categoryId?: never;
    parentSport?: never;
}

interface CategoryResult {
    id: string;
    name: string;
    type: 'category';
    categoryId: string;
    parentSport: {
        id: string;
        name: string;
    };
}

type SportSearchResult = SportResult | CategoryResult;

const eventService = EventService.getInstance();

// Función para buscar deportes y categorías en todos los idiomas
// Solo busca en deportes que tienen eventos registrados (usando cachedSports)
const searchSports = (pattern: string, lang: string, cachedSports: Array<{ idDeporte: number; nombre: string }> | null): SportSearchResult[] => {
    const results: SportSearchResult[] = [];
    const lowerPattern = pattern.toLowerCase();

    // Si no hay deportes con eventos, retornar vacío
    if (!cachedSports || cachedSports.length === 0) {
        return [];
    }

    // Crear un Set con los IDs de deportes que tienen eventos
    const availableSportIds = new Set(cachedSports.map(sport => sport.idDeporte.toString()));

    // Mapear idiomas a sus traducciones
    const allTranslations = {
        en: enTranslations.sportsTranslations,
        es: esTranslations.sportsTranslations,
        pt: ptTranslations.sportsTranslations
    };

    // Obtener las traducciones para el idioma actual (para mostrar los nombres)
    const currentLangTranslations = allTranslations[lang as keyof typeof allTranslations] || allTranslations.en;

    // Set para evitar duplicados
    const addedSports = new Set<string>();
    const addedCategories = new Set<string>();

    // Buscar en deportes de todos los idiomas, pero solo en los que tienen eventos
    Object.entries(allTranslations).forEach(([langKey, translations]) => {
        // Buscar en nombres de deportes (solo los que tienen eventos)
        Object.entries(translations.names).forEach(([sportId, sportName]) => {
            // Filtrar solo deportes que tienen eventos
            if (availableSportIds.has(sportId) && sportName.toLowerCase().includes(lowerPattern) && !addedSports.has(sportId)) {
                const displayName = currentLangTranslations.names[sportId] || sportName;
                results.push({
                    id: sportId,
                    name: displayName,
                    type: 'sport'
                });
                addedSports.add(sportId);
            }
        });

        // Buscar en categorías de deportes (solo de deportes que tienen eventos)
        Object.entries(translations.subsports).forEach(([sportId, categories]) => {
            // Solo buscar categorías de deportes que tienen eventos
            if (!availableSportIds.has(sportId)) {
                return;
            }

            Object.entries(categories).forEach(([categoryId, categoryName]) => {
                const uniqueCategoryKey = `${sportId}-${categoryId}`;

                if (categoryName.toLowerCase().includes(lowerPattern) && !addedCategories.has(uniqueCategoryKey)) {
                    const displayCategoryName = currentLangTranslations.subsports[sportId]?.[categoryId] || categoryName;
                    const displaySportName = currentLangTranslations.names[sportId] || translations.names[sportId] || 'Unknown Sport';

                    results.push({
                        id: uniqueCategoryKey,
                        name: displayCategoryName,
                        type: 'category',
                        categoryId,
                        parentSport: {
                            id: sportId,
                            name: displaySportName
                        }
                    });
                    addedCategories.add(uniqueCategoryKey);

                    // También agregar el deporte padre si no está ya agregado
                    if (!addedSports.has(sportId)) {
                        results.push({
                            id: sportId,
                            name: displaySportName,
                            type: 'sport'
                        });
                        addedSports.add(sportId);
                    }
                }
            });
        });
    });

    return results.slice(0, 20); // Limitar a 20 resultados para mostrar más categorías
};

// Componente interno para renderizar cada evento
interface EventItemProps {
    event: Event;
    lang: string;
    onResultClick: () => void;
    onResultMouseDown: (e: React.MouseEvent) => void;
}

const EventItem: React.FC<EventItemProps> = ({ event, lang, onResultClick, onResultMouseDown }) => {
    const { data: provinces, loading: provincesLoading } = useProvinces(lang, event.countryId);

    // Crear array de tags similar al EventCard
    const eventTags = [];
    if (event.sport) {
        eventTags.push(event.sport);
    }
    if (event.subsports) {
        event.subsports.forEach(subsport => {
            eventTags.push(subsport);
        });
    }

    return (
        <Link
            href={`/${lang}/event/${event.slug}`}
            onClick={onResultClick}
            onMouseDown={onResultMouseDown}
            className="block px-3 py-2 hover:bg-secondary/50 transition-colors border-b border-border/30 last:border-b-0"
        >
            <div className="flex items-start space-x-2">
                <Image
                    src={event.urlImage || getSportDefaultImage(event.sport, 'thumb')}
                    alt={event.title}
                    width={40}
                    height={28}
                    className="w-10 h-7 object-cover rounded flex-shrink-0"
                    onError={(e) => {
                        if (event.urlImage) {
                            (e.target as HTMLImageElement).src = getSportDefaultImage(event.sport, 'thumb');
                        }
                    }}
                />
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground truncate">
                        {event.title}
                    </p>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                        <span className="flex items-center">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                            </svg>
                            {new Date(event.startedDateIso).toLocaleDateString(lang, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                            })}
                        </span>
                        {!provincesLoading && provinces && event.provinceId && (
                            <span className="flex items-center">
                                <svg className="w-3 h-3 mr-1" viewBox="0 0 16 17" fill="none">
                                    <path d="M7.99927 1.06006H8.00024C9.0728 1.06016 10.1237 1.3386 11.0413 1.86377C11.9588 2.38901 12.7075 3.14103 13.2112 4.03564C13.7145 4.92984 13.9558 5.9354 13.9114 6.94678C13.8669 7.9583 13.5382 8.94194 12.9573 9.79346L12.928 9.8374L12.9055 9.88135C12.9055 9.88135 12.9052 9.88213 12.9045 9.8833L12.7991 10.0073L12.7854 10.0229L12.7727 10.0396C12.6418 10.2114 12.5002 10.3756 12.3489 10.5317L12.3176 10.5669L8.07446 15.5298C8.06889 15.5363 8.0601 15.5443 8.04712 15.5503C8.03384 15.5563 8.01731 15.5601 8.00024 15.5601H7.99829C7.9814 15.56 7.9656 15.5562 7.95239 15.5503L7.92407 15.5308L3.55493 10.4331L3.55005 10.4272L3.54419 10.4204L3.2981 10.1313L3.29126 10.1226L3.28345 10.1138L3.18384 9.99658L3.18286 9.99561L3.177 9.98682L3.15259 9.94189L3.12231 9.8999C2.51283 9.05302 2.15854 8.06504 2.09399 7.04346C2.02951 6.0218 2.25752 5.00099 2.75513 4.09131C3.25289 3.18144 4.00265 2.41519 4.927 1.87939C5.85179 1.34348 6.91466 1.05905 7.99927 1.06006ZM8.00024 3.56006C7.14796 3.56006 6.32477 3.88505 5.71313 4.47217C5.10045 5.06032 4.75024 5.8649 4.75024 6.71045C4.75032 7.34031 4.94497 7.95362 5.30591 8.47217C5.66659 8.99023 6.17576 9.38983 6.76587 9.62451C7.35588 9.85912 8.0037 9.91949 8.62817 9.80029C9.25283 9.68102 9.83099 9.38584 10.2874 8.94775C10.7439 8.5094 11.0589 7.9474 11.1868 7.33057C11.3147 6.71338 11.2483 6.07397 10.9983 5.49463C10.7485 4.91577 10.3279 4.42591 9.79419 4.0835C9.2607 3.7413 8.63632 3.5601 8.00024 3.56006Z" stroke="currentColor" strokeWidth="1.5" />
                                </svg>
                                {provinces.find(p => p.id === event.provinceId)?.value}
                            </span>
                        )}
                    </div>
                    {eventTags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                            {eventTags.slice(0, 3).map((tag, index) => (
                                <span key={`${tag}-${index}`} className="inline-flex items-center bg-primary/10 px-1.5 py-0.5 rounded text-xs">
                                    {capitalizeString(tag, true)}
                                </span>
                            ))}
                            {eventTags.length > 3 && (
                                <span className="text-xs text-muted-foreground">+{eventTags.length - 3}</span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Link>
    );
};

const SearchBar: React.FC<SearchBarProps> = ({
    placeholder = "",
    className = "",
    lang,
}) => {
    const router = useRouter();
    const translations = getTranslations(lang).searchBar;
    const cachedSports = useCachedSports();

    const [searchValue, setSearchValue] = useState('');
    const searchValueRef = useRef(searchValue);
    searchValueRef.current = searchValue;

    // Estados para eventos futuros
    const [futureEvents, setFutureEvents] = useState<Event[]>([]);
    const [futureHasMoreResults, setFutureHasMoreResults] = useState(false);
    const [futureIsLoading, setFutureIsLoading] = useState(false);

    // Estados para eventos pasados
    const [pastEvents, setPastEvents] = useState<Event[]>([]);
    const [pastHasMoreResults, setPastHasMoreResults] = useState(false);
    const [pastIsLoading, setPastIsLoading] = useState(false);

    // Estados para deportes
    const [sportsResults, setSportsResults] = useState<SportSearchResult[]>([]);

    // Estados generales
    const [showResults, setShowResults] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [activeSection, setActiveSection] = useState<'events' | 'sports'>('events');
    const [activeEventsTab, setActiveEventsTab] = useState<'future' | 'past'>('future');

    // Ocupación de plazas de los resultados (futuros + pasados) en un único request. La key del
    // hook es la lista de ids, así que solo se pide cuando el resultado de búsqueda cambia de
    // verdad, no en cada tecla ni al abrir/cerrar el desplegable.
    const registrationAvailability = useEventsRegistrationAvailability(
        [...futureEvents, ...pastEvents].map(e => e?.eventId)
    );

    const debouncedSearchValue = useDebounce(searchValue, 500);

    const searchRef = useRef<HTMLDivElement>(null);
    const resultsRef = useRef<HTMLDivElement>(null);

	const prevSearchValueTrackRef = useRef<string>(null);

    useClickOutside(searchRef, () => {
        setShowResults(false);
    });

    useEffect(() => {
        const resultsElement = resultsRef.current;
        if (!resultsElement || !showResults) return;

        // Función para prevenir el scroll del documento cuando el contenedor de resultados está abierto
        const preventDocumentWheel = (e: WheelEvent) => {
            e.preventDefault();
            e.stopPropagation();
        };

        const preventDocumentTouch = (e: TouchEvent) => {
            // Solo prevenir si no estamos dentro del contenedor de resultados
            if (!resultsElement.contains(e.target as HTMLElement)) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        // Función para manejar el scroll dentro del contenedor de resultados
        const handleContainerWheel = (e: WheelEvent) => {
            const target = e.target as HTMLElement;

            // Si el scroll está en el contenedor principal de resultados, permitirlo
            if (target === resultsElement || resultsElement.contains(target)) {
                const { scrollTop, scrollHeight, clientHeight } = resultsElement;

                // Si intentamos hacer scroll hacia arriba y ya estamos en la parte superior
                if (scrollTop === 0 && e.deltaY < 0) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }

                // Si intentamos hacer scroll hacia abajo y ya estamos en la parte inferior
                if (scrollTop + clientHeight >= scrollHeight && e.deltaY > 0) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }

                // Permitir scroll natural dentro del contenedor principal, pero prevenir propagación
                e.stopPropagation();
                return;
            }

            // Si estamos dentro de una sección scrollable específica (max-h-60)
            const scrollableSection = target.closest('.max-h-60') as HTMLDivElement;
            if (scrollableSection) {
                const { scrollTop, scrollHeight, clientHeight } = scrollableSection;

                // Si intentamos hacer scroll hacia arriba y ya estamos en la parte superior
                if (scrollTop === 0 && e.deltaY < 0) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }

                // Si intentamos hacer scroll hacia abajo y ya estamos en la parte inferior
                if (scrollTop + clientHeight >= scrollHeight && e.deltaY > 0) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                }

                // Permitir scroll natural dentro de la sección, pero prevenir propagación
                e.stopPropagation();
                return;
            }

            // Si estamos dentro de un contenedor con scroll horizontal (overflow-x-auto)
            const horizontalScrollContainer = target.closest('.overflow-x-auto') as HTMLDivElement;
            if (horizontalScrollContainer) {
                // Convertir scroll vertical en scroll horizontal
                const scrollAmount = e.deltaY;
                const currentScrollLeft = horizontalScrollContainer.scrollLeft;
                const maxScrollLeft = horizontalScrollContainer.scrollWidth - horizontalScrollContainer.clientWidth;

                // Aplicar scroll horizontal solo si hay espacio para scrollear
                if ((scrollAmount > 0 && currentScrollLeft < maxScrollLeft) ||
                    (scrollAmount < 0 && currentScrollLeft > 0)) {
                    horizontalScrollContainer.scrollLeft += scrollAmount;
                }

                // Prevenir comportamiento por defecto y propagación
                e.preventDefault();
                e.stopPropagation();
                return;
            }

            // Para cualquier otro scroll dentro del contenedor, prevenirlo
            e.preventDefault();
            e.stopPropagation();
        };

        // Función para manejar el touch dentro del contenedor de resultados
        const handleContainerTouch = (e: TouchEvent) => {
            // Permitir el comportamiento natural de touch dentro del contenedor
            e.stopPropagation();
        };

        // Agregar event listeners
        document.addEventListener('wheel', preventDocumentWheel, { passive: false });
        document.addEventListener('touchmove', preventDocumentTouch, { passive: false });
        resultsElement.addEventListener('wheel', handleContainerWheel, { passive: false });
        resultsElement.addEventListener('touchmove', handleContainerTouch, { passive: false });

        return () => {
            document.removeEventListener('wheel', preventDocumentWheel);
            document.removeEventListener('touchmove', preventDocumentTouch);
            resultsElement.removeEventListener('wheel', handleContainerWheel);
            resultsElement.removeEventListener('touchmove', handleContainerTouch);
        };
    }, [showResults]);

    useEffect(() => {
        const searchEvents = async () => {
            if (searchValueRef.current.trim().length < 2) {
                handleClearResults();
                return;
            }

            // Buscar deportes (síncrono) - solo en deportes que tienen eventos
            const sports = searchSports(debouncedSearchValue.trim(), lang, cachedSports);
            setSportsResults(sports);

            // Búsqueda de eventos futuros
            setFutureIsLoading(true);
            const searchFutureEvents = async () => {
                try {
                    const filters: EventFilters = {
                        pattern: debouncedSearchValue.trim(),
                        dateFilter: { type: 'future-events' },
                        sort: { field: 'date', order: 'asc' }
                    };

                    const response = await eventService.getEventList(1, 4, filters);

                    if (response && !response.error && response.data.items) {
                        const items = [...response.data.items];

                        if (items.length > 0 && items.length < 4) {
                            for (let i = 0; i < 4 - response.data.items.length; i++) {
                                items.push(null);
                            }
                        }

                        setFutureEvents(items);
                        setFutureHasMoreResults(response.data.items.length === 4);
                    } else {
                        setFutureEvents([]);
                        setFutureHasMoreResults(false);
                    }
                } catch (error) {
                    console.error('Error searching future events:', error);
                    setFutureEvents([]);
                    setFutureHasMoreResults(false);
                } finally {
                    setFutureIsLoading(false);
                }
            };

            // Búsqueda de eventos pasados
            setPastIsLoading(true);
            const searchPastEvents = async () => {
                try {
                    const filters: EventFilters = {
                        pattern: debouncedSearchValue.trim(),
                        dateFilter: { type: 'past-events' },
                        sort: { field: 'date', order: 'desc' }
                    };

                    const response = await eventService.getEventList(1, 4, filters);

                    if (response && !response.error && response.data.items) {
                        const items = [...response.data.items];

                        if (items.length > 0 && items.length < 4) {
                            for (let i = 0; i < 4 - response.data.items.length; i++) {
                                items.push(null);
                            }
                        }

                        setPastEvents(items);
                        setPastHasMoreResults(response.data.items.length === 4);
                    } else {
                        setPastEvents([]);
                        setPastHasMoreResults(false);
                    }
                } catch (error) {
                    console.error('Error searching past events:', error);
                    setPastEvents([]);
                    setPastHasMoreResults(false);
                } finally {
                    setPastIsLoading(false);
                }
            };

            // Ejecutar ambas búsquedas en paralelo
            await Promise.all([searchFutureEvents(), searchPastEvents()]);

            if (searchValueRef.current.trim().length < 2) {
                return;
            }

			const currentSearchValue = debouncedSearchValue.trim();

			if (currentSearchValue !== prevSearchValueTrackRef.current) {
				// Notificar a Facebook CAPI que el usuario ha realizado una búsqueda.
				// Fire-and-forget: no bloqueamos la visualización de resultados.
				// Solo se llama al endpoint si el usuario aceptó cookies de publicidad (CookieYes: advertisement=yes).
				if (hasAdvertisementConsent()) {
					const { fbp, fbc } = getFacebookBrowserIds();
					eventService.trackSearch(
						debouncedSearchValue.trim().toLowerCase(),
						fbp,
						fbc,
						window.location.href,
						true
					).catch(() => {});
				}
				// GA4 `search`. Fuera del gate de publicidad: depende de la categoría `analytics`,
				// que es otra decisión del usuario. Comparte el guard de "término ya reportado"
				// para no contar una búsqueda por cada tecla del debounce.
				trackGa4Search(currentSearchValue);
				prevSearchValueTrackRef.current = currentSearchValue;
			}

            // Mostrar resultados si hay algún evento o deporte
            setShowResults(true);
        };

        if (isFocused) {
            searchEvents();
        }
		else {
			prevSearchValueTrackRef.current = debouncedSearchValue.trim();
		}
    }, [debouncedSearchValue, isFocused, lang, cachedSports]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
    };

    const handleResultMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
    };

    const handleShowMore = (fromEnterKeyPress: boolean = false) => {
        if (fromEnterKeyPress || activeEventsTab === 'future') {
            handleClearSearch()
            router.push(`/${lang}/allEvents?dateFilter=future-events&pattern=${searchValueRef.current.trim()}&order=asc`);
        } else {
            handleClearSearch()
            router.push(`/${lang}/allEvents?dateFilter=past-events&pattern=${searchValueRef.current.trim()}&order=desc`);
        }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleShowMore(true)
        }
    };

    const handleSectionChange = (section: 'events' | 'sports') => {
        setActiveSection(section);
    };

    const handleEventsTabChange = (tab: 'future' | 'past') => {
        setActiveEventsTab(tab);
    };

    const handleInputFocus = () => {
        setIsFocused(true);
        if ((futureEvents.length > 0 || pastEvents.length > 0 || sportsResults.length > 0) && debouncedSearchValue.trim().length >= 2) {
            setShowResults(true);
        }
    };

    const handleInputBlur = () => {
        setTimeout(() => {
            setIsFocused(false);
            setShowResults(false);
        }, 150);
    };

    const handleClearResults = () => {
        setFutureEvents([]);
        setPastEvents([]);
        setSportsResults([]);
        setShowResults(false);
        setFutureHasMoreResults(false);
        setPastHasMoreResults(false);
    };

    const handleClearSearch = () => {
        handleClearResults();
        setSearchValue('');
        setActiveEventsTab('future');
    };

    const handleSportClick = (sport: SportSearchResult) => {
        // Obtener los nombres en inglés para la navegación
        if (sport.type === 'sport') {
            // Para deportes, navegar a la página del deporte
            const englishSportName = enTranslations.sportsTranslations.names[sport.id];
            if (englishSportName) {
                router.push(`/${lang}/sport/${createSportSlug(englishSportName)}`);
            }
        } else if (sport.type === 'category') {
            // Para categorías, navegar a la página de categoría dentro del deporte
            const englishSportName = enTranslations.sportsTranslations.names[sport.parentSport.id];
            const englishCategoryName = enTranslations.sportsTranslations.subsports[sport.parentSport.id]?.[sport.categoryId];

            if (englishSportName && englishCategoryName) {
                router.push(`/${lang}/sport/${createSportSlug(englishSportName)}/categories/${createCategorySlug(englishCategoryName)}`);
            }
        }

        setShowResults(false);
        setSearchValue('');
    };


    const sportsSearchValueSorting = (a: SportSearchResult, b: SportSearchResult) => {
        if (a.type === 'sport' && b.type === 'category') {
            return -1;
        }

        if (a.type === 'category' && b.type === 'sport') {
            return 1;
        }

        return a.name.localeCompare(b.name)
    };

    return (
        <div ref={searchRef} className={`${className}`}>
            <div className="relative">
                {searchValue.length === 0 && (
                    <div className="absolute left-0 top-0 bottom-0 flex items-center px-4 pointer-events-none">
                        <SearchIcon className="w-4 h-4 text-muted-foreground" />
                    </div>
                )}
                <input
                    type="text"
                    placeholder={placeholder}
                    value={searchValue}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    onKeyDown={handleInputKeyDown}
                    className={`w-full h-[38px] rounded-md border-0 bg-secondary dark:bg-secondary py-2 text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary ${
                        searchValue.length === 0 
                            ? isFocused 
                                ? 'pl-4 pr-4 search-input-focused-empty' 
                                : 'pl-10 pr-4'
                            : 'px-4'
                    }`}
                />

                {
                    searchValue.length > 0 && (
                        <button className="absolute right-0 top-0 bottom-0 px-4 py-2" onClick={handleClearSearch}>
                            <XIcon className="w-4 h-4" />
                        </button>
                    )
                }
            </div>


            {showResults && (futureEvents.length > 0 || pastEvents.length > 0 || sportsResults.length > 0 || futureIsLoading || pastIsLoading) && (
                <>
                    <div
                        className="hidden md:block fixed inset-0 bg-black/50 z-40"
                        style={{ top: 'var(--header-height, 80px)' }}
                        onClick={() => setShowResults(false)}
                    />

                    <div
                        className="block md:hidden fixed inset-0 bg-black/50 z-40"
                        style={{ top: 'var(--header-height, 160px)' }}
                        onClick={() => setShowResults(false)}
                    />

                    <div
                        ref={resultsRef}
                        className="fixed top-36 md:top-20 left-0 right-0 bg-background border border-border rounded-md shadow-2xl w-[100vw] max-h-[calc(100vh-160px)] md:max-h-[calc(100vh-100px)] overflow-y-auto z-50 p-4"
                        style={{ WebkitOverflowScrolling: 'touch' }}
                    >
                        <div className="block md:hidden">
                            <div className="space-y-4">
                                {/* 1. Search pattern button */}
                                <button
                                    onClick={() => handleShowMore(true)}
                                    onMouseDown={handleResultMouseDown}
                                    className="w-full text-left px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors flex items-center gap-2 border border-border/30"
                                >
                                    <SearchIcon className="w-4 h-4 flex-shrink-0" />
                                    <span className="truncate">&quot;{debouncedSearchValue.trim()}&quot;</span>
                                </button>

                                {/* 2. Botones de eventos futuros/pasados horizontalmente */}
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => {
                                            handleSectionChange('events');
                                            handleEventsTabChange('future');
                                        }}
                                        onMouseDown={handleResultMouseDown}
                                        className={`flex-1 px-3 py-2 rounded text-sm transition-colors ${activeSection === 'events' && activeEventsTab === 'future'
                                            ? 'bg-primary text-primary-foreground'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50 border border-border/30'
                                            }`}
                                    >
                                        {translations.upcoming}
                                    </button>
                                    <button
                                        onClick={() => {
                                            handleSectionChange('events');
                                            handleEventsTabChange('past');
                                        }}
                                        onMouseDown={handleResultMouseDown}
                                        className={`flex-1 px-3 py-2 rounded text-sm transition-colors ${activeSection === 'events' && activeEventsTab === 'past'
                                            ? 'bg-primary text-primary-foreground'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50 border border-border/30'
                                            }`}
                                    >
                                        {translations.past}
                                    </button>
                                </div>

                                {/* 3. 4 eventos encontrados usando EventItem */}
                                {activeEventsTab === 'future' && (
                                    <div>
                                        {futureIsLoading && futureEvents.length === 0 ? (
                                            <div className="p-3 text-sm text-muted-foreground text-center border border-border/30 rounded-md">
                                                {translations.searching_upcoming_events}
                                            </div>
                                        ) : futureEvents.filter(event => event !== null).length > 0 ? (
                                            <div className="border border-border/30 rounded-md overflow-hidden">
                                                {futureEvents.filter(event => event !== null).map((event) => (
                                                    <EventItem
                                                        key={`future-mobile-${event.eventId}`}
                                                        event={event}
                                                        lang={lang}
                                                        onResultClick={() => {
                                                            setShowResults(false);
                                                            setSearchValue('');
                                                            setActiveEventsTab('future');
                                                            setActiveSection('events');
                                                        }}
                                                        onResultMouseDown={handleResultMouseDown}
                                                    />
                                                ))}
                                            </div>
                                        ) : debouncedSearchValue.trim().length >= 2 ? (
                                            <div className="p-3 text-sm text-muted-foreground text-center border border-border/30 rounded-md">
                                                {translations.no_upcoming_events}
                                            </div>
                                        ) : null}
                                    </div>
                                )}

                                {activeEventsTab === 'past' && (
                                    <div>
                                        {pastIsLoading && pastEvents.length === 0 ? (
                                            <div className="p-3 text-sm text-muted-foreground text-center border border-border/30 rounded-md">
                                                {translations.searching_past_events}
                                            </div>
                                        ) : pastEvents.filter(event => event !== null).length > 0 ? (
                                            <div className="border border-border/30 rounded-md overflow-hidden">
                                                {pastEvents.filter(event => event !== null).map((event) => (
                                                    <EventItem
                                                        key={`past-mobile-${event.eventId}`}
                                                        event={event}
                                                        lang={lang}
                                                        onResultClick={() => {
                                                            setShowResults(false);
                                                            setSearchValue('');
                                                            setActiveEventsTab('future');
                                                            setActiveSection('events');
                                                        }}
                                                        onResultMouseDown={handleResultMouseDown}
                                                    />
                                                ))}
                                            </div>
                                        ) : debouncedSearchValue.trim().length >= 2 ? (
                                            <div className="p-3 text-sm text-muted-foreground text-center border border-border/30 rounded-md">
                                                {translations.no_past_events}
                                            </div>
                                        ) : null}
                                    </div>
                                )}

                                {/* 4. Botón "Mostrar más" */}
                                {((activeEventsTab === 'future' && futureHasMoreResults) || (activeEventsTab === 'past' && pastHasMoreResults)) && (
                                    <button
                                        onClick={() => { handleShowMore() }}
                                        onMouseDown={handleResultMouseDown}
                                        className="w-full px-3 py-2 text-sm text-primary hover:bg-secondary/50 transition-colors font-medium border border-border/30 rounded-md"
                                    >
                                        {translations.show_more}
                                    </button>
                                )}

                                {/* 5. Resultados de deportes y categorías */}
                                {sportsResults.length > 0 && (
                                    <div>
                                        <h3 className="px-3 py-2 text-sm font-semibold text-foreground border-b border-border/30">
                                            {translations.sports}
                                        </h3>
                                        <div className="border border-border/30 rounded-md mt-2 overflow-hidden">
                                            {sportsResults.sort(sportsSearchValueSorting).map((sport) => (
                                                <button
                                                    key={sport.id}
                                                    onClick={() => handleSportClick(sport)}
                                                    onMouseDown={handleResultMouseDown}
                                                    className="w-full text-left px-3 py-2 hover:bg-secondary/50 transition-colors border-b border-border/30 last:border-b-0"
                                                >
                                                    <div className="truncate">
                                                        <span className="font-medium text-foreground">
                                                            {sport.name}
                                                        </span>
                                                        {sport.type === 'category' && (
                                                            <span className="text-sm ml-2 text-muted-foreground">
                                                                ({sport.parentSport.name})
                                                            </span>
                                                        )}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Mensaje cuando no hay resultados en absoluto */}
                                {!futureIsLoading && !pastIsLoading && futureEvents.length === 0 && pastEvents.length === 0 && sportsResults.length === 0 && debouncedSearchValue.trim().length >= 2 && (
                                    <div className="p-3 text-sm text-muted-foreground text-center bg-secondary/10 rounded-md border border-border/30">
                                        {translations.no_results_found}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="hidden md:flex md:flex-row">
                            {/* Navigation section - 20% */}
                            <div className="w-1/5 pr-4 border-r border-border/30">
                                <div className="space-y-4">
                                    {/* Secciones principales */}
                                    <div className="space-y-4">
                                        {/* Sección Eventos */}
                                        <div>
                                            <div className="px-3 py-2 text-sm font-semibold text-foreground">
                                                {translations.events}
                                            </div>
                                            <div className="ml-2 mt-1 space-y-1">
                                                <button
                                                    onClick={() => {
                                                        handleSectionChange('events');
                                                        handleEventsTabChange('future');
                                                    }}
                                                    onMouseDown={handleResultMouseDown}
                                                    className={`w-full text-left px-2 py-1 rounded text-xs transition-colors ${activeSection === 'events' && activeEventsTab === 'future'
                                                        ? 'bg-primary text-primary-foreground'
                                                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                                                        }`}
                                                >
                                                    {translations.upcoming}
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        handleSectionChange('events');
                                                        handleEventsTabChange('past');
                                                    }}
                                                    onMouseDown={handleResultMouseDown}
                                                    className={`w-full text-left px-2 py-1 rounded text-xs transition-colors ${activeSection === 'events' && activeEventsTab === 'past'
                                                        ? 'bg-primary text-primary-foreground'
                                                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                                                        }`}
                                                >
                                                    {translations.past}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Sección Deportes */}
                                        <div>
                                            <div className="px-3 py-2 text-sm font-semibold text-foreground">
                                                {translations.sports}
                                            </div>
                                            <div className="ml-2 mt-1 max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
                                                <div className="space-y-1 pr-1">
                                                    {sportsResults.length > 0 ? (
                                                        sportsResults.sort(sportsSearchValueSorting).map((sport) => (
                                                            <button
                                                                key={sport.id}
                                                                onClick={() => handleSportClick(sport)}
                                                                onMouseDown={handleResultMouseDown}
                                                                className="w-full text-left px-2 py-1 rounded text-xs transition-colors text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                                                            >
                                                                <div className="truncate">
                                                                    <span className="font-medium">
                                                                        {sport.name}
                                                                    </span>
                                                                    {sport.type === 'category' && (
                                                                        <span className="text-xs ml-1 opacity-75">
                                                                            ({sport.parentSport.name})
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </button>
                                                        ))
                                                    ) : debouncedSearchValue.trim().length >= 2 ? (
                                                        <div className="px-2 py-1 text-xs text-muted-foreground italic">
                                                            {translations.no_results}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Search pattern button */}
                                    <button
                                        onClick={() => handleShowMore(true)}
                                        onMouseDown={handleResultMouseDown}
                                        className="w-full text-left px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-colors flex items-center gap-2 border-t border-border/30 pt-3"
                                    >
                                        <SearchIcon className="w-3 h-3 flex-shrink-0" />
                                        <span className="truncate">&quot;{debouncedSearchValue.trim()}&quot;</span>
                                    </button>
                                </div>
                            </div>

                            <div className="w-4/5 pl-4">
                                {/* Contenido de la sección Eventos */}
                                {activeSection === 'events' && activeEventsTab === 'future' && (
                                    <div>
                                        {futureIsLoading && futureEvents.length === 0 ? (
                                            <div className="p-3 text-sm text-muted-foreground text-center">
                                                {translations.searching_upcoming_events}
                                            </div>
                                        ) : futureEvents.length > 0 ? (
                                            <>
                                                <div className="space-y-2">
                                                    <div className='flex flex-row p-4 flex-wrap justify-between items-center' style={{
                                                        gap: 40,
                                                    }}>
                                                        {futureEvents.map((event, index) => {
                                                            if (event === null) {
                                                                return <div key={index} className='flex-1' />
                                                            }

                                                            return (
                                                                <div
                                                                    key={event.eventId}
                                                                    className={`flex-1 flex flex-col items-center`}
                                                                >
                                                                    <EventCard
                                                                        event={event}
                                                                        lang={lang}
                                                                        navigationContext={{
                                                                            type: 'all-events'
                                                                        }}
                                                                        availability={registrationAvailability[event.eventId]}
                                                                        onClick={() => {
                                                                            setShowResults(false);
                                                                            setSearchValue('');
                                                                            setActiveEventsTab('future');
                                                                            setActiveSection('events');
                                                                        }}
                                                                    />
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                                {futureHasMoreResults && (
                                                    <button
                                                        onClick={() => { handleShowMore() }}
                                                        onMouseDown={handleResultMouseDown}
                                                        className="w-full mt-4 px-3 py-2 text-sm text-primary hover:bg-secondary/50 transition-colors font-medium border border-border/30 rounded-md"
                                                    >
                                                        {translations.show_more}
                                                    </button>
                                                )}
                                            </>
                                        ) : debouncedSearchValue.trim().length >= 2 ? (
                                            <div className="p-3 text-sm text-muted-foreground text-center">
                                                {translations.no_upcoming_events}
                                            </div>
                                        ) : null}
                                    </div>
                                )}

                                {activeSection === 'events' && activeEventsTab === 'past' && (
                                    <div>
                                        {pastIsLoading && pastEvents.length === 0 ? (
                                            <div className="p-3 text-sm text-muted-foreground text-center">
                                                {translations.searching_past_events}
                                            </div>
                                        ) : pastEvents.length > 0 ? (
                                            <>
                                                <div className="space-y-2">
                                                    <div className='flex flex-row p-4 flex-wrap justify-between items-center' style={{
                                                        gap: 40,
                                                    }}>
                                                        {pastEvents.map((event, index) => {
                                                            if (event === null) {
                                                                return <div key={index} className='flex-1' />
                                                            }

                                                            return (
                                                                <div
                                                                    key={event.eventId}
                                                                    className={`flex-1 flex flex-col items-center`}
                                                                >
                                                                    <EventCard
                                                                        event={event}
                                                                        lang={lang}
                                                                        navigationContext={{
                                                                            type: 'all-events'
                                                                        }}
                                                                        availability={registrationAvailability[event.eventId]}
                                                                        onClick={() => {
                                                                            setShowResults(false);
                                                                            setSearchValue('');
                                                                            setActiveEventsTab('future');
                                                                            setActiveSection('events');
                                                                        }}
                                                                    />
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                                {pastHasMoreResults && (
                                                    <button
                                                        onClick={() => { handleShowMore() }}
                                                        onMouseDown={handleResultMouseDown}
                                                        className="w-full mt-4 px-3 py-2 text-sm text-primary hover:bg-secondary/50 transition-colors font-medium border border-border/30 rounded-md"
                                                    >
                                                        {translations.show_more}
                                                    </button>
                                                )}
                                            </>
                                        ) : debouncedSearchValue.trim().length >= 2 ? (
                                            <div className="p-3 text-sm text-muted-foreground text-center">
                                                {translations.no_past_events}
                                            </div>
                                        ) : null}
                                    </div>
                                )}

                                {/* Mensaje cuando no hay resultados en absoluto */}
                                {!futureIsLoading && !pastIsLoading && futureEvents.length === 0 && pastEvents.length === 0 && sportsResults.length === 0 && debouncedSearchValue.trim().length >= 2 && (
                                    <div className="p-3 text-sm text-muted-foreground text-center bg-secondary/10 rounded-md border border-border/30">
                                        {translations.no_results_found}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default SearchBar;
